import { BeneficiaryUpdateDto } from '../../../modules/beneficiaries/dtos/beneficiary-update';
import { OrganizationDto } from '../../../modules/organizations/dtos/organization';
import {
	DownloadActionFileResult,
	OperationType,
	UploadStatusFilter,
} from '../../../shared/enums/batch-actions.enum';
import { FileUploadResult } from '../../../shared/enums/file-upload-result.enum';
import { BatchAction, BatchActionInfo } from '../models/batch-actions.model';
import {
	BeneficiaryBenefitData,
	BeneficiaryInfoResponse,
	DependentRegistration,
	OrgBeneficiary,
	OrgBeneficiaryPendencies,
	TitularRegistration,
	UserDataModel,
} from '../models/beneficiaries.model';
import { BenefitResponse, BenefitSummary } from '../models/benefits.model';
import { INotification } from '../models/notification';
import {
	AddressResult,
	DashBeneInfo,
	DashChartInfo,
	DashReportChart,
	GroupsListSummary,
	HeadquarterSummary,
	OrgGroup,
	OrgHubItem,
	OrgListSummary,
	OrgRegistration,
	OrgUser,
	PartnerRegistration,
	UserRegistration,
} from '../models/organizations.model';
import {
	PolicyRegistrationResponse,
	PolicySummary,
} from '../models/policies.model';
import { PositionSummary } from '../models/positions.model';
import { ProductsSummary } from '../models/products.model';
import { Score } from '../models/score.model';

export enum LoadStatus {
	ERROR = 'error',
	IDLE = 'idle',
	LOADING = 'loading',
	SUCCESS = 'success',
}

export interface BatchActionsFilters {
	operationType: 'all' | OperationType;
	status: UploadStatusFilter;
}

export interface OrganizationsState {
	address: AddressResult | null;
	auxCities: string[];
	batchAction: BatchActionInfo | null;
	batchActionFile: Blob | null;
	batchActionFileResult: DownloadActionFileResult;
	batchActionFileUrl: string | null;
	batchActionLoadStatus: LoadStatus;
	batchActions: BatchAction[] | null;
	batchActionsFilters: BatchActionsFilters;
	beneNewStatus: string | null;
	beneficiaries: OrgBeneficiary[] | null;
	beneficiariesBulkFileUploadResult: FileUploadResult;
	beneficiariesCompleteCount: number;
	beneficiariesInactiveDeletedCount: number;
	beneficiariesPendencyCount: number;
	beneficiary: BeneficiaryUpdateDto | null;
	beneficiaryAllBenefits: BeneficiaryInfoResponse | null;
	beneficiaryAllPolicies: BeneficiaryInfoResponse | null;
	beneficiaryBenefitsResult: boolean | null;
	beneficiaryComplementaryAction: number;
	beneficiaryFileURL: string | null;
	beneficiaryFilesResult: number;
	beneficiaryPolicies: any | null;
	beneficiaryPoliciesResult: boolean | null;
	beneficiaryView: TitularRegistration | null;
	beneficiaryWallets: BeneficiaryBenefitData[] | null;
	benefit: BenefitResponse | null;
	benefits: BenefitSummary[] | null;
	cities: string[];
	cloneId: string | null;
	cnpjAvailability: boolean | null;
	cpfAvailability: boolean | null;
	cpfBeneficiario: number | null;
	cpfDependent: boolean | null;
	cpfTitular: boolean | null;
	dashBeneInfo: DashBeneInfo | null;
	dashCharts: DashReportChart[] | null;
	dashUrl: DashChartInfo | null;
	dashUrls: DashChartInfo[] | null;
	deleteStep: number;
	depDeleteResult: boolean | null;
	depModalResult: boolean | null;
	dependent: DependentRegistration | null;
	dependentBenefits: BeneficiaryInfoResponse | null;
	dependentBenefitsResult: boolean | null;
	dependentWallets: BeneficiaryBenefitData[] | null;
	emailAvailability: boolean | null;
	emailDependent: boolean | null;
	favOrgAction: number;
	groupList: GroupsListSummary[];
	headquarters: HeadquarterSummary[];
	loading: boolean;
	loadingPendencies: boolean;
	newBeneCpf: string | null;
	newBeneId: string | null;
	notifications: INotification[];
	orgBeneficiariesDataUrl: string | null;
	orgBeneficiaryPendencies: OrgBeneficiaryPendencies | null;
	orgBeneficiaryPendenciesResult: boolean;
	orgBeneficiaryPendencyResult: boolean;
	orgList: OrgListSummary[];
	organization: OrganizationDto | null;
	organizations: OrgGroup[];
	orghub: OrgHubItem[] | null;
	partner: PartnerRegistration | null;
	partners: PartnerRegistration[] | null;
	policies: PolicySummary[] | null;
	policy: PolicyRegistrationResponse | null;
	position: any | null;
	positions: PositionSummary[] | null;
	products: ProductsSummary[];
	registration: OrgRegistration | null;
	restoreForm: boolean;
	scoreXlsxUrl: string | null;
	scores: Score[];
	user: UserRegistration | null;
	userData: UserDataModel | null;
	users: OrgUser[] | null;
	xlsxUrl: string | null;
}

export const initialState: OrganizationsState = {
	address: null,
	auxCities: [],
	batchAction: null,
	batchActionFile: null,
	batchActionFileResult: DownloadActionFileResult.DEFAULT,
	batchActionFileUrl: null,
	batchActionLoadStatus: LoadStatus.IDLE,
	batchActions: null,
	batchActionsFilters: {
		operationType: 'all',
		status: UploadStatusFilter.ALL,
	},
	beneNewStatus: null,
	beneficiaries: null,
	beneficiariesBulkFileUploadResult: FileUploadResult.DEFAULT,
	beneficiariesCompleteCount: 0,
	beneficiariesInactiveDeletedCount: 0,
	beneficiariesPendencyCount: 0,
	beneficiary: null,
	beneficiaryAllBenefits: null,
	beneficiaryAllPolicies: null,
	beneficiaryBenefitsResult: null,
	beneficiaryComplementaryAction: -1,
	beneficiaryFileURL: null,
	beneficiaryFilesResult: 0,
	beneficiaryPolicies: null,
	beneficiaryPoliciesResult: null,
	beneficiaryView: null,
	beneficiaryWallets: null,
	benefit: null,
	benefits: null,
	cities: [],
	cloneId: null,
	cnpjAvailability: null,
	cpfAvailability: null,
	cpfBeneficiario: null,
	cpfDependent: null,
	cpfTitular: null,
	dashBeneInfo: null,
	dashCharts: null,
	dashUrl: null,
	dashUrls: null,
	deleteStep: 0,
	depDeleteResult: null,
	depModalResult: null,
	dependent: null,
	dependentBenefits: null,
	dependentBenefitsResult: null,
	dependentWallets: null,
	emailAvailability: null,
	emailDependent: null,
	favOrgAction: 0,
	groupList: [],
	headquarters: [],
	loading: false,
	loadingPendencies: false,
	newBeneCpf: null,
	newBeneId: null,
	notifications: [],
	orgBeneficiariesDataUrl: null,
	orgBeneficiaryPendencies: null,
	orgBeneficiaryPendenciesResult: false,
	orgBeneficiaryPendencyResult: false,
	orgList: [],
	organization: null,
	organizations: [],
	orghub: null,
	partner: null,
	partners: null,
	policies: null,
	policy: null,
	position: null,
	positions: null,
	products: [],
	registration: null,
	restoreForm: false,
	scoreXlsxUrl: null,
	scores: [],
	user: null,
	userData: null,
	users: null,
	xlsxUrl: null,
};
